import SkeletonBox from "src/components/SkeletonBox/SkeletonBox";
import i18n from "src/i18n";
import { getCssVarHex } from "src/utility";

const COLORS = {
  SUCCESS_GREEN: getCssVarHex("--color-font-success"),
  WARNING_YELLOW: getCssVarHex("--color-font-warning"),
  FAIL_RED: getCssVarHex("--color-font-danger"),
  UNKNOWN_BROWN: getCssVarHex("--color-font-disabled"),
};

const SUCCESS_STATUS = {
  get label() {
    return i18n.t("UserHistory.common.success");
  },
  color: COLORS.SUCCESS_GREEN,
};

const EXPIRED_STATUS = {
  get label() {
    return i18n.t("UserHistory.common.expired");
  },
  color: COLORS.FAIL_RED,
};

type OrderStatusKey = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 404;

type UserOrdersModuleType = "GiftCards" | "Discounts" | "PointsExchange";

type UserOrderItem<TModule extends UserOrdersModuleType = UserOrdersModuleType> = {
  code: string;
  name: string;
  brand: string | null;
  imageUrl: string;
  cardCode: string | null;
  cardPin: string | null;
  cardExpiryDate: string | null;
  moduleType: TModule;
  provider: string;
  points: number;
  value: number;
  isExpired: boolean;
};

type UserOrder<TItem> = {
  externalTransactionId: string;
  referenceNumber: string;
  status: string;
  statusEnum: OrderStatusKey;
  orderedAt: string;
  orderItems: TItem[];
};

type UserOrdersResponse<TItem> = {
  count: number;
  pagesNumber: number;
  orders: UserOrder<TItem>[];
};

type UserOrdersRequest = {
  Page: number;
  pageSize: number;
  moduleType: UserOrdersModuleType;
  from: string;
  to: string;
  lang: string;
};

type OrderStatusEntry = {
  readonly label: string;
  color: string;
};

const STATUS_MAP: Record<OrderStatusKey, OrderStatusEntry> = {
  1: { get label() { return i18n.t("UserHistory.common.success"); },   color: COLORS.SUCCESS_GREEN },
  2: { get label() { return i18n.t("UserHistory.common.failed"); },    color: COLORS.FAIL_RED },
  3: { get label() { return i18n.t("UserHistory.common.pending"); },   color: COLORS.WARNING_YELLOW },
  4: { get label() { return i18n.t("UserHistory.common.canceled"); },  color: COLORS.FAIL_RED },
  5: { get label() { return i18n.t("UserHistory.common.booked"); },    color: COLORS.SUCCESS_GREEN },
  6: { get label() { return i18n.t("UserHistory.common.ticketed"); },  color: COLORS.SUCCESS_GREEN },
  7: { get label() { return i18n.t("UserHistory.common.confirmed"); }, color: COLORS.SUCCESS_GREEN },
  404: { get label() { return i18n.t("UserHistory.common.unknown"); },   color: COLORS.UNKNOWN_BROWN },
};

const UNKNOWN_STATUS = STATUS_MAP[404];

const CARD_SKELETON = (
  <article className="divide-border-subtle bg-background-default flex w-full flex-col gap-2 divide-y rounded-md p-2 shadow-md">
    <header className="flex items-center justify-between p-4">
      <div className="flex min-w-0 items-center gap-2">
        <SkeletonBox className="h-10 w-10 shrink-0 rounded-xl" />
        <SkeletonBox className="h-6 w-28 rounded-sm sm:w-36" />
      </div>
      <SkeletonBox className="h-7 w-20 shrink-0 rounded-full" />
    </header>
    <section className="flex flex-wrap items-start justify-start gap-2 p-5">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex w-full shrink-0 grow-0 flex-col gap-1 sm:w-[calc((100%-0.5rem)/2)] md:w-[calc((100%-1rem)/3)]">
          <SkeletonBox className="h-3.5 w-20 rounded sm:w-24" />
          <SkeletonBox className="h-6 w-28 rounded sm:w-32" />
        </div>
      ))}
    </section>
    <footer className="flex items-center justify-end p-3">
      <SkeletonBox className="h-10 w-full rounded-md sm:w-36" />
    </footer>
  </article>
);

export type { OrderStatusKey, OrderStatusEntry, UserOrdersModuleType, UserOrderItem, UserOrder,UserOrdersResponse, UserOrdersRequest };
export { COLORS, SUCCESS_STATUS, EXPIRED_STATUS, STATUS_MAP, UNKNOWN_STATUS, CARD_SKELETON };
